import { useMemo } from "react"
import { useTeamStore } from "@/lib/hooks/useTeam"
import { useAssistantModule } from "@/components/assistant/context"
import type { ModuleCatalogEntry, ToolPolicy } from "@/lib/assistant/types"
import { DASHBOARD_INSTRUCTIONS, DASHBOARD_MULTI_PROJECT } from "./prompt"
import type { RawContact, RawMessage, TelerivetProject, OverviewAnalytics, DailyTrend, FlagBreakdown } from "@/pages/dev/dashboard/components/types"
import type { PinnedVarConfig } from "@/pages/dev/dashboard/hooks/useVariablesAnalytics"
import { useDataAssistantTools, type AssistantCommentItem, type AssistantCorrectedResponseItem } from "./tools"

// The Dashboard's loaded messaging data: analytics, search, pinned variables and reports

export const dashboardCatalog: ModuleCatalogEntry = {
  id: "dashboard",
  title: "Dashboard data",
  route: "/dashboard",
  description: "Conversation analytics: messages, contacts, escalations, ratings, variables and PDF reports.",
  resource: "dashboard",
}

// Built inside a React hook from the page's loaded data, so the schema check reads its source instead
export const DASHBOARD_TOOLS_SOURCE = "src/lib/assistant/modules/dashboard/tools.ts"

export interface DashboardAssistantProps {
  overviewAnalytics: OverviewAnalytics
  dailyTrends: DailyTrend[]
  flagBreakdown: FlagBreakdown[]
  contacts: RawContact[]
  messages: RawMessage[]
  commentsList?: AssistantCommentItem[]
  correctedResponsesList?: AssistantCorrectedResponseItem[]
  dateRangeLabel: string
  projects: TelerivetProject[]
  allProjects: TelerivetProject[]
  allVarKeys?: { contact: string[]; message: string[]; form?: string[] }
  pinnedVarKeys?: string[]
  pinnedVarConfigs?: Record<string, PinnedVarConfig>
  onTogglePin?: (key: string) => void
  onUpdatePinnedConfig?: (key: string, updates: Partial<PinnedVarConfig>) => void
  formFieldMeta?: Record<string, { title: string; type?: string; optionLabels?: Record<string, string> }>
}

// Pinning only changes what the dashboard shows, so it doesn't need an approval prompt
const PIN: ToolPolicy = { kind: "write", permission: ["dashboard", "read"], selfReviewed: true }
const WRITES: Record<string, ToolPolicy> = {
  pin_variable: PIN,
  pin_variables: PIN,
  unpin_variable: PIN,
  configure_pinned_variable: PIN,
}

// The forms module already has list_forms; this one lists the dashboard's form data
const RENAMED: Record<string, string> = { list_forms: "dash_list_forms" }


// Registers the module while the Dashboard has data loaded. Renders nothing: the assistant panel is the UI
export function DashboardAssistant(props: DashboardAssistantProps) {
  const { selectedTeam } = useTeamStore()
  const { contacts, messages, projects, allProjects, commentsList = [], correctedResponsesList = [], dateRangeLabel } = props

  const contactMap = useMemo(() => {
    const map = new Map<string, RawContact>()
    contacts.forEach(c => map.set(c.id, c))
    return map
  }, [contacts])

  const { createTools } = useDataAssistantTools({
    ...props,
    contactMap,
    teamId: selectedTeam?.id,
  })

  useAssistantModule("dashboard", () => {
    const raw: Record<string, any> = createTools()
    const tools: Record<string, any> = {}
    for (const [name, t] of Object.entries(raw)) tools[RENAMED[name] ?? name] = t

    const policies: Record<string, ToolPolicy> = {}
    for (const name of Object.keys(tools)) {
      policies[name] = WRITES[name] ?? { kind: "read", permission: ["dashboard", "read"] }
    }

    const filtered = projects.length < allProjects.length
    const others = allProjects.filter(p => !projects.some(v => v.id === p.id)).map(p => p.name)
    const context = [
      `Projects in view: ${projects.map(p => p.name).join(", ") || "none"}${filtered ? ` (also loaded but filtered out: ${others.join(", ")}; the user changes this with the project filter)` : ""}`,
      `${contacts.length} contacts, ${messages.length} messages, ${commentsList.length} reviewer comments, ${correctedResponsesList.length} corrected responses`,
      `Date range: ${dateRangeLabel}`,
    ].join("\n")

    return {
      id: "dashboard",
      title: "Dashboard data",
      tools,
      policies,
      instructions: allProjects.length > 1
        ? `${DASHBOARD_INSTRUCTIONS}\n${DASHBOARD_MULTI_PROJECT}`
        : DASHBOARD_INSTRUCTIONS,
      context,
    }
  })

  return null
}
